import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { NO_IMAGE_PLACEHOLDER_URI } from '@/components/icons/CustomIcons';

interface LoadingImageProps {
  src: string;
  alt: string;
  className?: string;
  fallbackSrc?: string;
  minHeight?: string;
}

export const LoadingImage: React.FC<LoadingImageProps> = ({
  src,
  alt,
  className,
  fallbackSrc = NO_IMAGE_PLACEHOLDER_URI,
  minHeight = 'min-h-[200px]',
}) => {
  const [loaded, setLoaded] = useState(false);
  // Track the currently displayed src to avoid flash when src changes
  // (e.g. base64 preview -> S3 URL). Keep showing old image until new one loads.
  const [displaySrc, setDisplaySrc] = useState(src);
  const pendingSrcRef = useRef(src);

  useEffect(() => {
    if (src === displaySrc) return;
    if (!loaded) {
      // Still loading the first image - just update directly
      setDisplaySrc(src);
      return;
    }
    // Already showing an image - preload new src in background
    pendingSrcRef.current = src;
    const img = new Image();
    img.onload = () => {
      // Only update if this is still the latest src requested
      if (pendingSrcRef.current === src) {
        setDisplaySrc(src);
      }
    };
    img.onerror = () => {
      if (pendingSrcRef.current === src) {
        setDisplaySrc(fallbackSrc);
      }
    };
    img.src = src;
  }, [src]);

  return (
    <div className={cn('relative w-full h-full overflow-hidden', !loaded && minHeight)}>
      {!loaded && (
        <div className="absolute inset-0 rounded-lg overflow-hidden bg-gray-200">
          <div className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-[shimmer_1.5s_infinite]" />
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
            </svg>
          </div>
        </div>
      )}
      <img
        src={displaySrc}
        alt={alt}
        className={cn(className, loaded ? 'opacity-100 transition-opacity duration-300' : 'invisible')}
        onLoad={() => setLoaded(true)}
        onError={(e) => { e.currentTarget.src = fallbackSrc; setLoaded(true); }}
      />
    </div>
  );
};

export default LoadingImage;
